import React, { useState, useEffect, useCallback } from 'react'
import { Container, Row, Col, Form, Button, Spinner, Alert } from "react-bootstrap";
import '../componentCss/webform.css';
import { useNavigate, useParams, Link } from "react-router-dom";
import Dashboardheader from './dashboardheader';
import Sidebar from './sidebar';
import '../componentCss/sidebar.css';
import { updateMasterTableFieldsAction, getSingleMasterTableDataAction, deleteMasterColumnAction } from '../../Action/admin.action';
import toast from 'react-hot-toast';
import { BsArrowLeftCircleFill, BsFillTrash3Fill } from "react-icons/bs";
import config from '../../config/config';
import Swal from "sweetalert2";
import BulkImportButton from './BulkImport/BulkImportButton';
import { decryptData } from './decrypt';
import { v4 as uuidv4 } from 'uuid';
import { editMasterTableSchema, validateForm, validateTableMetadata, transformRolesForValidation, transformColumnsForValidation, canSaveForm } from '../../validations/masterTableSchema';

const allowedTypes = [
    "string", "integer", "float", "boolean", "date", "datetime",
    "email", "phone", "url", "select", "multi_select", "text"
];

const Webform = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [formData, setForm] = useState({
        table_id: "",
        name: "",
        description: "",
        category: "",
        access_level: "public",
        allowed_roles: ""
    });

    const [editFieldList, setEditFieldList] = useState([]);
    const [newFieldList, setNewFieldList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(true);
    const [validationErrors, setValidationErrors] = useState({});
    const [formError, setFormError] = useState(null);

    useEffect(() => {
        if (id) {
            getMasterTableData();
        }
    }, [id]);


    const getMasterTableData = useCallback(async () => {
        if (!id) return;
        
        setIsFetching(true);
        setFormError(null);
        
        try {
            const res = await getSingleMasterTableDataAction(id);
            console.log("res table data", res);
            
            if (res && res.data) {
                const data = decryptData(res.data);
                
                // Populate form with fetched data
                setForm({
                    table_id: data.table_id || id,
                    name: data.name || "",
                    description: data.description || "",
                    category: data.category || "",
                    access_level: data.access_level || "public",
                    allowed_roles: Array.isArray(data.allowed_roles) 
                        ? data.allowed_roles.join(", ") 
                        : (data.allowed_roles || "")
                });

                // Process existing columns
                const updatedColumns = (data.columns || []).map((c, index) => ({
                    ...c,
                    column_id: c.column_id || `col_${index + 1}`,
                    max_length: c.max_length ? Math.max(1, Number(c.max_length)) : null,
                    min_length: c.min_length ? Number(c.min_length) : 0,
                    min_value: c.min_value !== undefined ? Number(c.min_value) : null,
                    max_value: c.max_value !== undefined ? Number(c.max_value) : null,
                    required: Boolean(c.required),
                    unique: Boolean(c.unique),
                    // Handle options: convert array to string for form display
                    options: Array.isArray(c.options) ? c.options.join(", ") : (c.options || ""),
                    description: c.description || "",
                    order: index + 1
                }));

                setEditFieldList(updatedColumns);
                
                // Clear any previous errors
                setValidationErrors({});
                
            } else {
                throw new Error("No data received from server");
            }
        } catch (error) {
            console.error("Error fetching master table data:", error);
            setFormError("Failed to load table data. Please try again.");
            toast.error("Failed to load table data");
        } finally {
            setIsFetching(false);
        }
    }, [id]);

    const handleTableChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        
        // Clear validation error for this field
        if (validationErrors[name]) {
            setValidationErrors(prev => {
                const updated = { ...prev };
                delete updated[name];
                return updated;
            });
        }
    };

    const handleEditFieldChange = (i, e) => {
        const { name, value, type, checked } = e.target;
        let updatedFields = [...editFieldList];
        
        if (type === "checkbox") {
            updatedFields[i][name] = checked;
        } else if (name === "max_length" || name === "min_length" || name === "min_value" || name === "max_value") {
            // Handle numeric fields
            updatedFields[i][name] = value === "" ? null : Number(value);
        } else if (name === "options") {
            // Handle options field - convert string to array for internal storage
            updatedFields[i][name] = value; // Keep as string for form display
        } else {
            updatedFields[i][name] = value;
        }
        
        setEditFieldList(updatedFields);
        
        // Clear validation errors for columns
        if (validationErrors[`columns.${i}.${name}`]) {
            setValidationErrors(prev => {
                const updated = { ...prev };
                delete updated[`columns.${i}.${name}`];
                return updated;
            });
        }
    };

    const handleNewFieldChange = (i, e) => {
        const { name, value, type, checked } = e.target;
        let updatedFields = [...newFieldList];
        
        if (type === "checkbox") {
            updatedFields[i][name] = checked;
        } else if (name === "max_length" || name === "min_length" || name === "min_value" || name === "max_value") {
            // Handle numeric fields
            updatedFields[i][name] = value === "" ? null : Number(value);
        } else if (name === "options") {
            // Handle options field - keep as string for form display
            updatedFields[i][name] = value;
        } else {
            updatedFields[i][name] = value;
        }
        
        setNewFieldList(updatedFields);
        
        // Clear validation errors for new columns
        const totalCols = editFieldList.length;
        if (validationErrors[`columns.${totalCols + i}.${name}`]) {
            setValidationErrors(prev => {
                const updated = { ...prev };
                delete updated[`columns.${totalCols + i}.${name}`];
                return updated;
            });
        }
    };

    const addNewField = () => {
        setNewFieldList([
            ...newFieldList,
            {
                column_id: `new_col_${Date.now()}`,
                name: "",
                data_type: "",
                required: false,
                unique: false,
                default_value: "",
                max_length: null,
                min_length: 0,
                min_value: null,
                max_value: null,
                options: "", // Keep as empty string for form display
                description: "",
                order: editFieldList.length + newFieldList.length + 1
            }
        ]);
    };

    const removeNewField = (i) => {
        let updated = [...newFieldList];
        updated.splice(i, 1);
        setNewFieldList(updated);
    };

    const deleteField = async (fieldName) => {
        Swal.fire({
            title: "Are you sure?",
            text: "This will permanently delete the field.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it!"
        }).then(async (result) => {
            if (result.isConfirmed) {
                setIsLoading(true);
                try {
                    const res = await deleteMasterColumnAction({ id, fieldName });
                    if (res && res.success) {
                        toast.success(res.msg || "Field deleted successfully");
                        await getMasterTableData(); // Refresh data
                    } else {
                        toast.error(res?.msg || "Failed to delete field");
                    }
                } catch (error) {
                    console.error("Error deleting field:", error);
                    toast.error("Failed to delete field. Please try again.");
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const validatePayload = async (payload, hasColumnChanges = false) => {
        // Transform payload for validation
        const validationPayload = transformRolesForValidation({
            ...payload,
            columns: hasColumnChanges ? transformColumnsForValidation(payload.columns || []) : undefined
        });
        
        // If no column changes, validate only metadata
        if (!hasColumnChanges) {
            const metadataValidation = await validateTableMetadata(validationPayload);
            return metadataValidation;
        }
        
        // Validate full form including columns
        const validation = await validateForm(validationPayload);
        return validation;
    };
    
    const buildPayload = () => {
        // Check if we have any column changes (existing columns modified or new columns added)
        const hasColumnChanges = newFieldList.length > 0 || editFieldList.some(f => 
            f.name && f.data_type // At least name and data_type are provided
        );
        
        // Build base payload with table metadata
        const payload = {
            name: formData.name?.trim(),
            description: formData.description?.trim() || null,
            category: formData.category?.trim() || null,
            access_level: formData.access_level || "public",
            allowed_roles: formData.allowed_roles
                ? formData.allowed_roles.split(",").map(s => s.trim()).filter(Boolean)
                : []
        };
        
        // Only include columns if there are changes (align with backend optional columns)
        if (hasColumnChanges) {
            const allColumns = [
                ...editFieldList.map((f, i) => ({
                    column_id: f.column_id,
                    name: f.name?.trim(),
                    data_type: f.data_type,
                    required: Boolean(f.required),
                    unique: Boolean(f.unique),
                    default_value: f.default_value === "" ? null : f.default_value,
                    max_length: f.max_length,
                    min_length: f.min_length || 0,
                    min_value: f.min_value,
                    max_value: f.max_value,
                    options: Array.isArray(f.options)
                        ? f.options.filter(opt => opt && opt.trim())
                        : (f.options || "").split(",").map(s => s.trim()).filter(Boolean),
                    description: f.description?.trim() || null,
                    order: i + 1
                })),
                ...newFieldList.map((f, i) => ({
                    column_id: f.column_id,
                    name: f.name?.trim(),
                    data_type: f.data_type,
                    required: Boolean(f.required),
                    unique: Boolean(f.unique),
                    default_value: f.default_value === "" ? null : f.default_value,
                    max_length: f.max_length,
                    min_length: f.min_length || 0,
                    min_value: f.min_value,
                    max_value: f.max_value,
                    options: Array.isArray(f.options)
                        ? f.options.filter(opt => opt && opt.trim())
                        : (f.options || "").split(",").map(s => s.trim()).filter(Boolean),
                    description: f.description?.trim() || null,
                    order: editFieldList.length + i + 1
                }))
            ].filter(col => col.name && col.data_type); // Only include valid columns
            
            payload.columns = allColumns;
        }
        
        return { payload, hasColumnChanges };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (isLoading) return;
        
        setIsLoading(true);
        setValidationErrors({});
        setFormError(null);
        
        try {
            // Build payload and check for column changes
            const { payload, hasColumnChanges } = buildPayload();
            
            console.log("Payload:", payload, "Has column changes:", hasColumnChanges);
            
            // Validate payload (metadata only if no column changes)
            const validation = await validatePayload(payload, hasColumnChanges);
            
            if (!validation.isValid) {
                setValidationErrors(validation.errors);
                console.error("Validation errors:", validation.errors);
                
                // Show first validation error in toast
                const firstError = Object.values(validation.errors)[0];
                toast.error(firstError || "Please fix the validation errors before submitting");
                return;
            }
            
            // Submit to backend
            const res = await updateMasterTableFieldsAction(id, payload);
            
            if (res && (res.success !== false)) {
                const successMessage = res.message || res.msg || "Master table updated successfully";
                toast.success(successMessage);
                setTimeout(() => {
                    navigate(`${config.baseUrl}master-data`);
                }, 1500);
            } else {
                // Handle backend validation errors
                const errorMessage = res?.message || res?.msg || res?.error || "Failed to update master table";
                console.error("Backend error:", res);
                toast.error(errorMessage);
                
                // If backend returns field-specific errors, set them
                if (res?.errors && typeof res.errors === 'object') {
                    setValidationErrors(res.errors);
                } else if (res?.detail && typeof res.detail === 'string') {
                    setFormError(res.detail);
                }
            }
        } catch (error) {
            console.error("Error updating master table:", error);
            
            // Handle network or other errors
            let errorMessage = "Failed to update master table. Please try again.";
            
            if (error.response?.status === 422) {
                // Handle validation errors from backend
                const detail = error.response?.data?.detail;
                if (Array.isArray(detail)) {
                    errorMessage = detail.map(err => err.msg || err.message).join(", ");
                } else if (typeof detail === 'string') {
                    errorMessage = detail;
                }
            } else if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail;
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            toast.error(errorMessage);
            setFormError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Dashboardheader />
            <div className="wrapper">
                <Sidebar />
                <div id="content">
                    <Container fluid className="p-4">
                        <Row>
                            <Col lg={12}>
                                <div className="innerhead mb-4">
                                    <h5 className='headtitle'>Edit Master Table</h5>
                                </div>
                                <Link to={`${config.baseUrl}master-data`}>
                                    <span style={{ float: 'right' }}>
                                        Back <BsArrowLeftCircleFill />
                                    </span>
                                </Link>
                            </Col>
                        </Row>

                        {formError && (
                            <Alert variant="danger" className="mb-3" dismissible onClose={() => setFormError(null)}>
                                {formError}
                            </Alert>
                        )}
                        
                        {isFetching ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </Spinner>
                                <p className="mt-2">Loading table data...</p>
                            </div>
                        ) : (
                            <Form onSubmit={handleSubmit}>
                            <Row className="mb-3">
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label>Table Name *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleTableChange}
                                            isInvalid={!!validationErrors.name}
                                            disabled={isLoading}
                                        />
                                        {validationErrors.name && (
                                            <Form.Control.Feedback type="invalid">
                                                {validationErrors.name}
                                            </Form.Control.Feedback>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label>Description</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            name="description"
                                            value={formData.description}
                                            onChange={handleTableChange}
                                            isInvalid={!!validationErrors.description}
                                            disabled={isLoading}
                                            maxLength={1000}
                                        />
                                        {validationErrors.description && (
                                            <Form.Control.Feedback type="invalid">
                                                {validationErrors.description}
                                            </Form.Control.Feedback>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label>Category</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="category"
                                            value={formData.category}
                                            onChange={handleTableChange}
                                            isInvalid={!!validationErrors.category}
                                            disabled={isLoading}
                                            maxLength={100}
                                        />
                                        {validationErrors.category && (
                                            <Form.Control.Feedback type="invalid">
                                                {validationErrors.category}
                                            </Form.Control.Feedback>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label>Access Level *</Form.Label>
                                        <Form.Select
                                            name="access_level"
                                            value={formData.access_level}
                                            onChange={handleTableChange}
                                            isInvalid={!!validationErrors.access_level}
                                            disabled={isLoading}
                                        >
                                            <option value="">Select Access Level</option>
                                            <option value="public">Public</option>
                                            <option value="private">Private</option>
                                            <option value="restricted">Restricted</option>
                                        </Form.Select>
                                        {validationErrors.access_level && (
                                            <Form.Control.Feedback type="invalid">
                                                {validationErrors.access_level}
                                            </Form.Control.Feedback>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={8}>
                                    <Form.Group>
                                        <Form.Label>Allowed Roles (comma-separated)</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="allowed_roles"
                                            value={formData.allowed_roles}
                                            onChange={handleTableChange}
                                            isInvalid={!!validationErrors.allowed_roles}
                                            disabled={isLoading}
                                            placeholder="admin, doctor, nurse"
                                        />
                                        <Form.Text className="text-muted">
                                            Enter role names separated by commas. Leave empty for no role restrictions.
                                        </Form.Text>
                                        {validationErrors.allowed_roles && (
                                            <Form.Control.Feedback type="invalid">
                                                {validationErrors.allowed_roles}
                                            </Form.Control.Feedback>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>

                            <hr />

                            <h5>Existing Columns</h5>
                            {editFieldList.length === 0 && !isFetching ? (
                                <p className="text-muted">No existing columns found.</p>
                            ) : (
                                editFieldList.map((field, index) => (
                                    <Row key={field.column_id || index} className="mb-3 border rounded p-3">
                                        <Col md={12} className="mb-2">
                                            <small className="text-muted">Column {index + 1} - {field.column_id}</small>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group>
                                                <Form.Label>Name *</Form.Label>
                                                <Form.Control
                                                    placeholder="Column Name"
                                                    name="name"
                                                    value={field.name || ""}
                                                    onChange={e => handleEditFieldChange(index, e)}
                                                    isInvalid={!!validationErrors[`columns.${index}.name`]}
                                                    disabled={isLoading}
                                                />
                                                {validationErrors[`columns.${index}.name`] && (
                                                    <Form.Control.Feedback type="invalid">
                                                        {validationErrors[`columns.${index}.name`]}
                                                    </Form.Control.Feedback>
                                                )}
                                            </Form.Group>
                                        </Col>
                                        <Col md={2}>
                                            <Form.Group>
                                                <Form.Label>Data Type *</Form.Label>
                                                <Form.Select
                                                    name="data_type"
                                                    value={field.data_type || ""}
                                                    onChange={e => handleEditFieldChange(index, e)}
                                                    isInvalid={!!validationErrors[`columns.${index}.data_type`]}
                                                    disabled={isLoading}
                                                >
                                                    <option value="">Select Type</option>
                                                    {allowedTypes.map(t => (
                                                        <option key={t} value={t}>{t.replace('_', ' ')}</option>
                                                    ))}
                                                </Form.Select>
                                                {validationErrors[`columns.${index}.data_type`] && (
                                                    <Form.Control.Feedback type="invalid">
                                                        {validationErrors[`columns.${index}.data_type`]}
                                                    </Form.Control.Feedback>
                                                )}
                                            </Form.Group>
                                        </Col>
                                        <Col md={2}>
                                            <Form.Group>
                                                <Form.Label>Constraints</Form.Label>
                                                <div>
                                                    <Form.Check
                                                        type="checkbox"
                                                        label="Required"
                                                        name="required"
                                                        checked={Boolean(field.required)}
                                                        onChange={e => handleEditFieldChange(index, e)}
                                                        disabled={isLoading}
                                                    />
                                                    <Form.Check
                                                        type="checkbox"
                                                        label="Unique"
                                                        name="unique"
                                                        checked={Boolean(field.unique)}
                                                        onChange={e => handleEditFieldChange(index, e)}
                                                        disabled={isLoading}
                                                    />
                                                </div>
                                            </Form.Group>
                                        </Col>
                                        <Col md={2}>
                                            <Form.Group>
                                                <Form.Label>Default Value</Form.Label>
                                                <Form.Control
                                                    placeholder="Default Value"
                                                    name="default_value"
                                                    value={field.default_value || ""}
                                                    onChange={e => handleEditFieldChange(index, e)}
                                                    disabled={isLoading}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={2}>
                                            <Form.Group>
                                                <Form.Label>Description</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={1}
                                                    placeholder="Description"
                                                    name="description"
                                                    value={field.description || ""}
                                                    onChange={e => handleEditFieldChange(index, e)}
                                                    disabled={isLoading}
                                                    maxLength={500}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={1} className="d-flex align-items-end">
                                            <Button 
                                                variant="danger" 
                                                size="sm" 
                                                onClick={() => deleteField(field.name)}
                                                disabled={isLoading}
                                                title="Delete Column"
                                            >
                                                <BsFillTrash3Fill />
                                            </Button>
                                        </Col>
                                        {(field.data_type === 'string' || field.data_type === 'text') && (
                                            <Col md={12} className="mt-2">
                                                <Row>
                                                    <Col md={6}>
                                                        <Form.Group>
                                                            <Form.Label>Min Length</Form.Label>
                                                            <Form.Control
                                                                type="number"
                                                                name="min_length"
                                                                value={field.min_length || ""}
                                                                onChange={e => handleEditFieldChange(index, e)}
                                                                min="0"
                                                                disabled={isLoading}
                                                            />
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={6}>
                                                        <Form.Group>
                                                            <Form.Label>Max Length</Form.Label>
                                                            <Form.Control
                                                                type="number"
                                                                name="max_length"
                                                                value={field.max_length || ""}
                                                                onChange={e => handleEditFieldChange(index, e)}
                                                                min="1"
                                                                disabled={isLoading}
                                                            />
                                                        </Form.Group>
                                                    </Col>
                                                </Row>
                                            </Col>
                                        )}
                                        {(field.data_type === 'integer' || field.data_type === 'float') && (
                                            <Col md={12} className="mt-2">
                                                <Row>
                                                    <Col md={6}>
                                                        <Form.Group>
                                                            <Form.Label>Min Value</Form.Label>
                                                            <Form.Control
                                                                type="number"
                                                                name="min_value"
                                                                value={field.min_value || ""}
                                                                onChange={e => handleEditFieldChange(index, e)}
                                                                step={field.data_type === 'float' ? '0.01' : '1'}
                                                                disabled={isLoading}
                                                            />
                                                        </Form.Group>
                                                    </Col>
                                                    <Col md={6}>
                                                        <Form.Group>
                                                            <Form.Label>Max Value</Form.Label>
                                                            <Form.Control
                                                                type="number"
                                                                name="max_value"
                                                                value={field.max_value || ""}
                                                                onChange={e => handleEditFieldChange(index, e)}
                                                                step={field.data_type === 'float' ? '0.01' : '1'}
                                                                disabled={isLoading}
                                                            />
                                                        </Form.Group>
                                                    </Col>
                                                </Row>
                                            </Col>
                                        )}
                                        {(field.data_type === 'select' || field.data_type === 'multi_select') && (
                                            <Col md={12} className="mt-2">
                                                <Form.Group>
                                                    <Form.Label>Options (comma-separated) *</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={2}
                                                        name="options"
                                                        value={Array.isArray(field.options) ? field.options.join(', ') : (field.options || '')}
                                                        onChange={e => handleEditFieldChange(index, e)}
                                                        placeholder="option1, option2, option3"
                                                        disabled={isLoading}
                                                    />
                                                    <Form.Text className="text-muted">
                                                        Enter options separated by commas for select fields.
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                        )}
                                    </Row>
                                ))
                            )}

                            <hr />

                            <h5>New Columns</h5>
                            {newFieldList.length === 0 ? (
                                <p className="text-muted">No new columns added. Click "Add New Column" to add one.</p>
                            ) : (
                                newFieldList.map((field, index) => {
                                    const totalCols = editFieldList.length;
                                    const fieldIndex = totalCols + index;
                                    return (
                                        <Row key={field.column_id || index} className="mb-3 border rounded p-3 bg-light">
                                            <Col md={12} className="mb-2">
                                                <small className="text-muted">New Column {index + 1} - {field.column_id}</small>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label>Name *</Form.Label>
                                                    <Form.Control
                                                        placeholder="Column Name"
                                                        name="name"
                                                        value={field.name || ""}
                                                        onChange={e => handleNewFieldChange(index, e)}
                                                        isInvalid={!!validationErrors[`columns.${fieldIndex}.name`]}
                                                        disabled={isLoading}
                                                    />
                                                    {validationErrors[`columns.${fieldIndex}.name`] && (
                                                        <Form.Control.Feedback type="invalid">
                                                            {validationErrors[`columns.${fieldIndex}.name`]}
                                                        </Form.Control.Feedback>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                            <Col md={2}>
                                                <Form.Group>
                                                    <Form.Label>Data Type *</Form.Label>
                                                    <Form.Select
                                                        name="data_type"
                                                        value={field.data_type || ""}
                                                        onChange={e => handleNewFieldChange(index, e)}
                                                        isInvalid={!!validationErrors[`columns.${fieldIndex}.data_type`]}
                                                        disabled={isLoading}
                                                    >
                                                        <option value="">Select Type</option>
                                                        {allowedTypes.map(t => (
                                                            <option key={t} value={t}>{t.replace('_', ' ')}</option>
                                                        ))}
                                                    </Form.Select>
                                                    {validationErrors[`columns.${fieldIndex}.data_type`] && (
                                                        <Form.Control.Feedback type="invalid">
                                                            {validationErrors[`columns.${fieldIndex}.data_type`]}
                                                        </Form.Control.Feedback>
                                                    )}
                                                </Form.Group>
                                            </Col>
                                            <Col md={2}>
                                                <Form.Group>
                                                    <Form.Label>Constraints</Form.Label>
                                                    <div>
                                                        <Form.Check
                                                            type="checkbox"
                                                            label="Required"
                                                            name="required"
                                                            checked={Boolean(field.required)}
                                                            onChange={e => handleNewFieldChange(index, e)}
                                                            disabled={isLoading}
                                                        />
                                                        <Form.Check
                                                            type="checkbox"
                                                            label="Unique"
                                                            name="unique"
                                                            checked={Boolean(field.unique)}
                                                            onChange={e => handleNewFieldChange(index, e)}
                                                            disabled={isLoading}
                                                        />
                                                    </div>
                                                </Form.Group>
                                            </Col>
                                            <Col md={2}>
                                                <Form.Group>
                                                    <Form.Label>Default Value</Form.Label>
                                                    <Form.Control
                                                        placeholder="Default Value"
                                                        name="default_value"
                                                        value={field.default_value || ""}
                                                        onChange={e => handleNewFieldChange(index, e)}
                                                        disabled={isLoading}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={2}>
                                                <Form.Group>
                                                    <Form.Label>Description</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={1}
                                                        placeholder="Description"
                                                        name="description"
                                                        value={field.description || ""}
                                                        onChange={e => handleNewFieldChange(index, e)}
                                                        disabled={isLoading}
                                                        maxLength={500}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={1} className="d-flex align-items-end">
                                                <Button 
                                                    variant="danger" 
                                                    size="sm" 
                                                    onClick={() => removeNewField(index)}
                                                    disabled={isLoading}
                                                    title="Remove Column"
                                                >
                                                    <BsFillTrash3Fill />
                                                </Button>
                                            </Col>
                                            {(field.data_type === 'string' || field.data_type === 'text') && (
                                                <Col md={12} className="mt-2">
                                                    <Row>
                                                        <Col md={6}>
                                                            <Form.Group>
                                                                <Form.Label>Min Length</Form.Label>
                                                                <Form.Control
                                                                    type="number"
                                                                    name="min_length"
                                                                    value={field.min_length || ""}
                                                                    onChange={e => handleNewFieldChange(index, e)}
                                                                    min="0"
                                                                    disabled={isLoading}
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={6}>
                                                            <Form.Group>
                                                                <Form.Label>Max Length</Form.Label>
                                                                <Form.Control
                                                                    type="number"
                                                                    name="max_length"
                                                                    value={field.max_length || ""}
                                                                    onChange={e => handleNewFieldChange(index, e)}
                                                                    min="1"
                                                                    disabled={isLoading}
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>
                                                </Col>
                                            )}
                                            {(field.data_type === 'integer' || field.data_type === 'float') && (
                                                <Col md={12} className="mt-2">
                                                    <Row>
                                                        <Col md={6}>
                                                            <Form.Group>
                                                                <Form.Label>Min Value</Form.Label>
                                                                <Form.Control
                                                                    type="number"
                                                                    name="min_value"
                                                                    value={field.min_value || ""}
                                                                    onChange={e => handleNewFieldChange(index, e)}
                                                                    step={field.data_type === 'float' ? '0.01' : '1'}
                                                                    disabled={isLoading}
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                        <Col md={6}>
                                                            <Form.Group>
                                                                <Form.Label>Max Value</Form.Label>
                                                                <Form.Control
                                                                    type="number"
                                                                    name="max_value"
                                                                    value={field.max_value || ""}
                                                                    onChange={e => handleNewFieldChange(index, e)}
                                                                    step={field.data_type === 'float' ? '0.01' : '1'}
                                                                    disabled={isLoading}
                                                                />
                                                            </Form.Group>
                                                        </Col>
                                                    </Row>
                                                </Col>
                                            )}
                                            {(field.data_type === 'select' || field.data_type === 'multi_select') && (
                                                <Col md={12} className="mt-2">
                                                    <Form.Group>
                                                        <Form.Label>Options (comma-separated) *</Form.Label>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={2}
                                                            name="options"
                                                            value={Array.isArray(field.options) ? field.options.join(', ') : (field.options || '')}
                                                            onChange={e => handleNewFieldChange(index, e)}
                                                            placeholder="option1, option2, option3"
                                                            disabled={isLoading}
                                                        />
                                                        <Form.Text className="text-muted">
                                                            Enter options separated by commas for select fields.
                                                        </Form.Text>
                                                    </Form.Group>
                                                </Col>
                                            )}
                                        </Row>
                                    );
                                })
                            )}
                            <div className="d-flex gap-2 mt-4">
                                <BulkImportButton
                                    tableId={id}
                                    tableName={formData.name}
                                    onImportComplete={() => getMasterTableData()}
                                    buttonText="Import Data from Excel"
                                />
                                <Button
                                    variant="secondary"
                                    onClick={addNewField}
                                    disabled={isLoading}
                                >
                                    Add New Column
                                </Button>
                                <Button
                                    variant="primary"
                                    type="submit"
                                    disabled={isLoading || !canSaveForm(formData, editFieldList, newFieldList)}
                                >
                                    {isLoading ? (
                                        <>
                                            <Spinner size="sm" animation="border" className="me-2" />
                                            Saving...
                                        </>
                                    ) : (
                                        'Save Changes'
                                    )}
                                </Button>
                                <Button
                                    variant="outline-secondary"
                                    type="button"
                                    onClick={() => navigate(`${config.baseUrl}master-data`)}
                                    disabled={isLoading}
                                >
                                    Cancel
                                </Button>
                            </div>
                            </Form>
                        )}
                    </Container>
                </div>
            </div>
        </>
    )
}

export default Webform;
